import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { ConteoPorClave } from "@/api/types";
import { useIncidentesEstadisticas } from "@/hooks/useIncidents";

const AZUL = "#3b82f6"; // open
const AMBAR = "#f59e0b"; // needs_human
const VERDE = "#22c55e"; // resolved
const GRIS = "#94a3b8";

// La base guarda `needs_human`, que no significa nada para quien lo lee.
// Significa: ningun playbook aplico, lo tiene que ver una persona.
const ETIQUETA_ESTADO: Record<string, string> = {
  open: "Abiertos",
  needs_human: "Requieren intervención",
  resolved: "Resueltos",
  ack: "Reconocidos",
  closed: "Cerrados",
};

const COLOR_ESTADO: Record<string, string> = {
  Abiertos: AZUL,
  "Requieren intervención": AMBAR,
  Resueltos: VERDE,
};

function Panel({
  titulo,
  ayuda,
  datos,
  color,
  porEstado = false,
}: {
  titulo: string;
  ayuda: string;
  datos: ConteoPorClave[];
  color?: string;
  porEstado?: boolean;
}) {
  const horizontal = porEstado;
  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="text-sm font-semibold">{titulo}</h3>
      <p className="mb-3 mt-1 text-xs text-muted-foreground">{ayuda}</p>
      {datos.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">Sin datos.</p>
      ) : (
        <ResponsiveContainer width="100%" height={horizontal ? 220 : 280}>
          <BarChart
            data={datos}
            layout={horizontal ? "horizontal" : "vertical"}
            margin={{ top: 4, right: 16, bottom: 4, left: horizontal ? 0 : 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            {horizontal ? (
              <>
                <XAxis dataKey="clave" fontSize={11} />
                <YAxis allowDecimals={false} fontSize={11} />
              </>
            ) : (
              <>
                <XAxis type="number" allowDecimals={false} fontSize={11} />
                <YAxis type="category" dataKey="clave" width={150} fontSize={11} />
              </>
            )}
            <Tooltip formatter={(v: number) => [v, "incidentes"]} contentStyle={{ fontSize: 12 }} />
            <Bar dataKey="total" radius={horizontal ? [3, 3, 0, 0] : [0, 3, 3, 0]}>
              {datos.map((d) => (
                <Cell key={d.clave} fill={color ?? COLOR_ESTADO[d.clave] ?? GRIS} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export function IncidentCharts() {
  const { data, isLoading, isError } = useIncidentesEstadisticas();

  if (isLoading || isError || !data) return null;

  const general = data.general.map((c) => ({
    clave: ETIQUETA_ESTADO[c.clave] ?? c.clave,
    total: c.total,
  }));

  // Los dos tops coinciden cuando no hay ningun incidente en `open`, que es lo
  // normal hoy: o se resuelve, o cae en `needs_human`. Decirlo evita que se lea
  // como que la pantalla repite una grafica por error.
  const abiertosIgualIntervencion =
    data.total_abiertos === data.total_intervencion && data.total_intervencion > 0;

  return (
    <div className="mb-6 grid gap-4 lg:grid-cols-2">
      <Panel
        titulo="Todos los incidentes, por estado"
        ayuda="Desde que el cierre automático está vivo, «abierto» significa que el problema sigue ahí: lo que se recupera se cierra solo."
        datos={general}
        porEstado
      />
      <Panel
        titulo={`Más resueltos · últimos ${data.ventana_resueltos_dias} días`}
        ayuda="Los equipos que más veces dieron problema y se recuperaron. Aquí se ven los reincidentes."
        datos={data.top_resueltos}
        color={VERDE}
      />
      <Panel
        titulo="Requieren intervención"
        ayuda="Ningún playbook aplicó: estos los tiene que ver una persona."
        datos={data.top_intervencion}
        color={AMBAR}
      />
      <Panel
        titulo="Abiertos ahora"
        ayuda={
          abiertosIgualIntervencion
            ? "Coincide con «requieren intervención»: hoy no hay ningún incidente en «abierto» — o se resuelve, o pasa a intervención."
            : "Todo lo que sigue sin resolver, con o sin playbook."
        }
        datos={data.top_abiertos}
        color={AZUL}
      />
    </div>
  );
}
